/**
 * AppHeader Component
 *
 * Main application header with navigation, theme switcher, and user menu.
 * Features include:
 * - Sidebar toggle button
 * - Search button with keyboard shortcut and recent searches modal
 * - Primary navigation links
 * - Notification and action icons
 * - Theme switcher (light/dark/auto)
 * - User dropdown menu
 * - Breadcrumb navigation
 * - Sticky positioning with scroll shadow effect
 *
 * @component
 * @example
 * return (
 *   <AppHeader />
 * )
 */

import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { api, getMenu, currentUser } from '../api/client'
import { fmtMoneda } from '../utils/moneda'
import { useTroniaVisible } from '../utils/troniaAcceso'
import {
  CAlert,
  CBadge,
  CButton,
  CFormTextarea,
  CContainer,
  CDropdown,
  CDropdownDivider,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormInput,
  CHeader,
  CHeaderNav,
  CHeaderToggler,
  CListGroup,
  CListGroupItem,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CSpinner,
  CModalTitle,
  CNavLink,
  CNavItem,
  CSearchButton,
  useColorModes,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import {
  cilApplications,
  cilBell,
  cilCloud,
  cilCloudDownload,
  cilCommentSquare,
  cilContrast,
  cilEnvelopeOpen,
  cilExternalLink,
  cilHistory,
  cilList,
  cilMenu,
  cilMoon,
  cilSearch,
  cilSun,
  cilX,
  cibProbot,
} from '@coreui/icons'

import { AppHeaderDropdown } from './header/index'
import { useNotificaciones, fmtFechaAviso } from './NotificacionesPanel'
import { t } from '../i18n'

/**
 * AppHeader functional component
 *
 * Manages header UI including:
 * - Redux integration for sidebar state
 * - Theme management with CoreUI useColorModes hook
 * - Scroll-based shadow effect
 * - Responsive navigation
 *
 * @returns {React.ReactElement} Header component with navigation and controls
 */
const AppHeader = () => {
  const headerRef = useRef()
  const { colorMode, setColorMode } = useColorModes('coreui-free-react-admin-template-theme')
  const [searchVisible, setSearchVisible] = useState(false)
  const navigate = useNavigate()
  // BUSCADOR GLOBAL [RGM]: clientes/préstamos/rutas (debounce 300ms).
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState(null)
  // NOTIFICACIONES [RGM]: avisos vivos de la empresa + aviso GLOBAL de nueva
  // versión del APK (✕ descarta local — opción (a) aprobada Rafael 2026-10-01;
  // al publicar un APK nuevo, el aviso reaparece).
  const { avisos: notifAvisos, descartar: descartarNotif } = useNotificaciones()
  // SOPORTE [RGM]: el mensaje al correo de soporte (Resend).
  const [soporteAbierto, setSoporteAbierto] = useState(false)
  const [soporteAsunto, setSoporteAsunto] = useState('')
  const [soporteMensaje, setSoporteMensaje] = useState('')
  const [soporteEnviando, setSoporteEnviando] = useState(false)
  const [soporteResp, setSoporteResp] = useState('')
  // [RGM] ATAJOS (decisión Rafael): CONTROL/COMMAND solo → buscador de las
  // opciones del MENÚ; "/" → TronIA. Sin K.
  const [menuOpciones, setMenuOpciones] = useState([])
  // [RGM] 2026-09-17 (regla Rafael): sin TronIA activo (o en mora) no se
  // muestran los enlaces a TronIA (menú Herramientas y atajo "/").
  const troniaOk = useTroniaVisible()
  // [RGM] Saldo vencido SUTIL en el navbar (pedido Rafael): recordatorio de las
  // cuentas de cobro impagas — el detalle está en Mi Cuenta → Pagos → Cuentas
  // de cobro. Solo empresas (el superadmin no tiene cuenta) y solo si > 0;
  // si la consulta falla, no se muestra nada.
  const [saldoVencido, setSaldoVencido] = useState(0)
  useEffect(() => {
    if (currentUser()?.role === 'superadmin') return
    api.get('/pagos/cuentas')
      .then((respuesta) => setSaldoVencido(Number(respuesta?.saldoVencido ?? 0)))
      .catch(() => {})
  }, [])

  useEffect(() => {
    getMenu()
      .then((respuesta) => {
        const opciones = []
        ;(respuesta?.items ?? []).forEach((grupo) => {
          ;(grupo.items ?? []).forEach((item) => {
            if (item.url && item.etiqueta !== 'Salir') {
              // [RGM] i18n Fase 1: las mismas etiquetas de menú que el sidebar
              // (chrome con fallback ES) — así el buscador en inglés encuentra
              // por el nombre VISIBLE (ej. "Loans", no solo "Prestamos").
              opciones.push({ nombre: t(item.etiqueta), url: item.url, grupo: t(grupo.grupo ?? '') })
            }
          })
        })
        setMenuOpciones(opciones)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    const enInput = (el) =>
      el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
    const onKeyDown = (e) => {
      // [RGM] 2026-10-01 (pedido Rafael): el buscador del menú se abre con
      // COMMAND+K (⌘K en Mac / Ctrl+K en Windows) — antes se abría con
      // Command/Control SOLO (se disparaba sin querer). Es el atajo estándar
      // del CSearchButton de CoreUI.
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setSearchVisible(true)
        return
      }
    }
    const onKeyUp = (e) => {
      // "/" → TronIA (solo si no se está escribiendo en un campo, y solo si
      // la empresa tiene TronIA — regla Rafael 2026-09-17).
      if (e.key === '/' && !enInput(e.target) && troniaOk) {
        e.preventDefault()
        navigate('/tronia')
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('keyup', onKeyUp)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('keyup', onKeyUp)
    }
  }, [navigate, troniaOk])

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults(null)
      return
    }
    const t = setTimeout(() => {
      api
        .get(`/buscar?q=${encodeURIComponent(searchQuery.trim())}`)
        .then(setSearchResults)
        .catch(() => setSearchResults(null))
    }, 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  const irA = (url) => {
    setSearchVisible(false)
    setSearchQuery('')
    setSearchResults(null)
    navigate(url)
  }

  const enviarSoporte = async () => {
    setSoporteEnviando(true)
    setSoporteResp('')
    try {
      const r = await api.post('/soporte', { asunto: soporteAsunto || 'Soporte SiprinIA', mensaje: soporteMensaje })
      setSoporteResp(r?.message ?? t('Mensaje enviado.'))
      setSoporteMensaje('')
      setSoporteAsunto('')
    } catch (err) {
      setSoporteResp(err?.message ?? t('No se pudo enviar el mensaje.'))
    } finally {
      setSoporteEnviando(false)
    }
  }

  const dispatch = useDispatch()
  const sidebarShow = useSelector((state) => state.sidebarShow)

  useEffect(() => {
    const handleScroll = () => {
      headerRef.current &&
        headerRef.current.classList.toggle('shadow-sm', document.documentElement.scrollTop > 0)
    }

    document.addEventListener('scroll', handleScroll)
    return () => document.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <CHeader position="sticky" className="mb-4 p-0" ref={headerRef}>
        <CContainer className="border-bottom px-4 position-relative" fluid>
        <CHeaderToggler
          onClick={() => dispatch({ type: 'set', sidebarShow: !sidebarShow })}
          style={{ marginInlineStart: '-14px' }}
        >
          <CIcon icon={cilMenu} size="lg" />
        </CHeaderToggler>
        <CSearchButton
          onTrigger={() => setSearchVisible(true)}
          // [RGM] 2026-10-06 (pedido Rafael): "Search" → "Buscar" según el
          // idioma del navegador. [RGM] i18n Fase 1: ahora por el módulo
          // compartido web/src/i18n (ES/EN automático por dispositivo).
          // Atajo REAL del buscador (⌘K/Ctrl+K — antes mostraba la "/" por
          // defecto del componente, que en la app abre TronIA).
          placeholder={t('Buscar')}
          shortcut="meta+k,ctrl+k"
          aria-label={t('Abrir buscador')}
          aria-controls="app-header-search-modal"
        />
        <CModal
          id="app-header-search-modal"
          visible={searchVisible}
          onClose={() => setSearchVisible(false)}
          aria-labelledby="app-header-search-modal-title"
        >
          <CModalHeader>
            <CModalTitle id="app-header-search-modal-title" className="w-100">
              <CFormInput
                type="search"
                placeholder={t('Buscar cliente, préstamo o ruta...')}
                aria-label={t('Buscar')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </CModalTitle>
          </CModalHeader>
          <CModalBody>
            {searchQuery.trim().length < 2 ? (
              <p className="text-body-secondary small mb-0">
                {t('Escribe al menos 2 letras: cédula o nombre del cliente, número de préstamo, o nombre/código de la ruta.')}
              </p>
            ) : !searchResults ? (
              <div className="text-center py-3">
                <CSpinner color="primary" size="sm" />
              </div>
            ) : (
              <>
                {/* OPCIONES DEL MENÚ que coinciden (el atajo Control/Command). */}
                {menuOpciones
                  .filter((o) =>
                    o.nombre.toLowerCase().includes(searchQuery.trim().toLowerCase()),
                  )
                  .slice(0, 6)
                  .map((o) => (
                    <CListGroupItem
                      key={`m-${o.url}-${o.nombre}`}
                      as="button"
                      type="button"
                      className="d-flex justify-content-between align-items-center"
                      onClick={() => irA(o.url)}
                    >
                      <span>{o.nombre}</span>
                      {o.grupo ? <CBadge color="secondary">{o.grupo}</CBadge> : null}
                    </CListGroupItem>
                  ))}
                {searchResults.clientes?.length > 0 && (
                  <>
                    <p className="text-body-secondary small mb-1">Clientes</p>
                    <CListGroup flush className="mb-2">
                      {searchResults.clientes.map((cl) => (
                        <CListGroupItem
                          key={`c-${cl.clienteCedula}`}
                          as="button"
                          type="button"
                          onClick={() => irA(`/clientes/${cl.clienteCedula}`)}
                        >
                          {cl.nombre} — C.C. {cl.clienteCedula}
                        </CListGroupItem>
                      ))}
                    </CListGroup>
                  </>
                )}
                {searchResults.prestamos?.length > 0 && (
                  <>
                    <p className="text-body-secondary small mb-1">Préstamos</p>
                    <CListGroup flush className="mb-2">
                      {searchResults.prestamos.map((p) => (
                        <CListGroupItem
                          key={`p-${p.prestamoNro}`}
                          as="button"
                          type="button"
                          onClick={() => irA(`/prestamos/${p.prestamoNro}`)}
                        >
                          Préstamo #{p.prestamoNro} — Cliente {p.clienteCedula}
                        </CListGroupItem>
                      ))}
                    </CListGroup>
                  </>
                )}
                {searchResults.rutas?.length > 0 && (
                  <>
                    <p className="text-body-secondary small mb-1">Rutas</p>
                    <CListGroup flush>
                      {searchResults.rutas.map((r) => (
                        <CListGroupItem
                          key={`r-${r.zonaCodigo}`}
                          as="button"
                          type="button"
                          onClick={() => irA(`/rutas/${r.zonaCodigo}`)}
                        >
                          {r.nombre}
                        </CListGroupItem>
                      ))}
                    </CListGroup>
                  </>
                )}
                {!searchResults.clientes?.length &&
                  !searchResults.prestamos?.length &&
                  !searchResults.rutas?.length && (
                    <p className="text-body-secondary small mb-0">{t('Sin resultados.')}</p>
                  )}
              </>
            )}
          </CModalBody>
        </CModal>
        {/* SOPORTE [RGM]: el mensaje va al correo de soporte (Resend). */}
        <CModal visible={soporteAbierto} onClose={() => setSoporteAbierto(false)}>
          <CModalHeader>
            <CModalTitle>{t('Contactar a soporte')}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {soporteResp && <CAlert color="info">{soporteResp}</CAlert>}
            <CFormInput
              className="mb-2"
              placeholder={t('Asunto')}
              value={soporteAsunto}
              onChange={(e) => setSoporteAsunto(e.target.value)}
            />
            <CFormTextarea
              rows={4}
              placeholder={t('Escribe tu mensaje...')}
              value={soporteMensaje}
              onChange={(e) => setSoporteMensaje(e.target.value)}
            />
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setSoporteAbierto(false)}>
              {t('Cerrar')}
            </CButton>
            <CButton color="primary" disabled={soporteEnviando || !soporteMensaje.trim()} onClick={enviarSoporte}>
              {soporteEnviando ? t('Enviando...') : t('Enviar')}
            </CButton>
          </CModalFooter>
        </CModal>

        {/* [RGM] Saldo vencido — CENTRADO en la barra del header (pedido Rafael
            2026-10-01): posición ABSOLUTA = no mueve nada de lo existente. */}
        {saldoVencido > 0 && (
          <div className="d-none d-md-block position-absolute top-50 start-50 translate-middle">
            <CNavLink
              href="#"
              className="text-danger text-nowrap py-1"
              style={{ fontSize: 12 }}
              title="Cuentas de cobro impagas — ver Mi Cuenta → Pagos"
              onClick={(e) => {
                e.preventDefault()
                navigate('/mi-cuenta')
              }}
            >
              Saldo vencido: {fmtMoneda(saldoVencido)}
            </CNavLink>
          </div>
        )}
        <CHeaderNav className="ms-auto">
          {/* [RGM] Herramientas — píldora outline azul (diseño aprobado):
              icono cilApplications + texto "Herramientas" (oculto < lg);
              menú: TronIA, Data Siprin, Auditoría, divisor, Clima/Divisas
              (externo, pestaña nueva). Oculto en móvil (d-none d-lg-flex). */}
          <CDropdown variant="nav-item" placement="bottom-end" className="d-none d-lg-flex">
            <CDropdownToggle
              caret={false}
              className="btn tr-herramientas-pill px-3 py-1 my-1"
            >
              <CIcon icon={cilApplications} size="lg" className="me-lg-2" />
              <span className="d-none d-lg-inline tr-herramientas-texto">{t('Herramientas')}</span>
            </CDropdownToggle>
            <CDropdownMenu className="pt-0" placement="bottom-end" alignment="end">
              <CDropdownHeader className="bg-body-secondary fw-semibold mb-2">
                {t('Herramientas')}
              </CDropdownHeader>
              {/* [RGM] 2026-09-17 (regla Rafael): enlace TronIA solo con
                  TronIA PRUEBA/ACTIVO y empresa sin mora. */}
              {troniaOk && (
                <CDropdownItem as="a" href="/tronia">
                  <CIcon icon={cilCommentSquare} className="me-2" /> TronIA
                </CDropdownItem>
              )}
              <CDropdownItem as="a" href="/data-siprin">
                <CIcon icon={cilSearch} className="me-2" /> Data Siprin
              </CDropdownItem>
              <CDropdownItem as="a" href="/auditoria">
                <CIcon icon={cilHistory} className="me-2" /> {t('Auditoría')}
              </CDropdownItem>
              <CDropdownDivider />
              <CDropdownItem as="a" href="https://clima.cybertronia.com/" target="_blank" rel="noopener">
                <CIcon icon={cilCloud} className="me-2" /> {t('Clima/Divisas')}
                <CIcon icon={cilExternalLink} className="ms-1" size="sm" />
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
          <CNavItem>
            <CDropdown variant="nav-item" placement="bottom-end">
              <CDropdownToggle caret={false}>
                {/* [RGM] 2026-09-17 (pedido Rafael): el badge va ARRIBA de la
                    campanita (superpuesto en la esquina superior derecha) —
                    antes quedaba al lado con ms-1. */}
                <span className="position-relative d-inline-flex">
                  <CIcon icon={cilBell} size="lg" />
                  {notifAvisos.length > 0 && (
                    <CBadge
                      color="danger"
                      shape="rounded-pill"
                      className="position-absolute top-0 start-100 translate-middle"
                      style={{ fontSize: '0.65rem' }}
                    >
                      {notifAvisos.length}
                    </CBadge>
                  )}
                </span>
              </CDropdownToggle>
              {/* [RGM] 2026-10-07 (pedido Rafael: "el título de Notificaciones
                  no llena de gris el borde de arriba"): sin padding superior
                  (pt-0) y con overflow oculto, la franja gris del título
                  llega hasta el borde superior del menú redondeado. */}
              <CDropdownMenu
                className="pt-0"
                style={{ minWidth: 320, overflow: 'hidden' }}
              >
                <CDropdownHeader className="bg-body-secondary fw-semibold">
                  {t('Notificaciones')} ({notifAvisos.length})
                </CDropdownHeader>
                {notifAvisos.length === 0 && (
                  <CDropdownItem disabled>{t('Sin novedades')}</CDropdownItem>
                )}
                {notifAvisos.map((a) => (
                  <div key={a.id} className="dropdown-item d-flex align-items-center gap-2 px-2">
                    <button
                      type="button"
                      className="btn btn-link p-0 text-start flex-grow-1 text-decoration-none text-body"
                      style={{ whiteSpace: 'normal' }}
                      onClick={() =>
                        a.descargar ? (window.location.href = a.descargar) : navigate(a.url ?? '/')
                      }
                    >
                      {/* [RGM] 2026-10-05 (pedido Rafael): la campana muestra
                          también la FECHA del aviso (antes solo el título).
                          2026-10-06: con HORA cuando el origen la trae
                          (nueva versión del APK). */}
                      <span className="d-block">{a.titulo}</span>
                      {a.fecha && (
                        <span className="d-block small text-body-secondary">
                          {fmtFechaAviso(a.fecha)}
                        </span>
                      )}
                    </button>
                    {a.descargar && (
                      <span
                        role="button"
                        title={t('Descargar')}
                        className="text-primary d-inline-flex"
                        onClick={() => {
                          window.location.href = a.descargar
                        }}
                      >
                        <CIcon icon={cilCloudDownload} size="sm" />
                      </span>
                    )}
                    <span
                      role="button"
                      title={t('Descartar')}
                      className="text-body-secondary d-inline-flex"
                      onClick={(e) => {
                        // [RGM] 2026-10-01 (reporte Rafael): descartar NO debe
                        // cerrar el menú desplegable — stopPropagation para que
                        // el clic no llegue al dropdown-item (auto-close).
                        e.stopPropagation()
                        descartarNotif(a.id)
                      }}
                    >
                      <CIcon icon={cilX} size="sm" />
                    </span>
                  </div>
                ))}
              </CDropdownMenu>
            </CDropdown>
          </CNavItem>

          <CNavItem>
            <CNavLink
              href="#"
              onClick={(e) => {
                e.preventDefault()
                setSoporteAbierto(true)
              }}
            >
              <CIcon icon={cilEnvelopeOpen} size="lg" />
            </CNavLink>
          </CNavItem>
        </CHeaderNav>
        <CHeaderNav>
          <li className="nav-item py-1">
            <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
          </li>
          <CDropdown variant="nav-item" placement="bottom-end">
            <CDropdownToggle caret={false}>
              {colorMode === 'dark' ? (
                <CIcon icon={cilMoon} size="lg" />
              ) : colorMode === 'auto' ? (
                <CIcon icon={cilContrast} size="lg" />
              ) : (
                <CIcon icon={cilSun} size="lg" />
              )}
            </CDropdownToggle>
            <CDropdownMenu>
              <CDropdownItem
                active={colorMode === 'light'}
                className="d-flex align-items-center"
                as="button"
                type="button"
                onClick={() => setColorMode('light')}
              >
                <CIcon className="me-2" icon={cilSun} size="lg" /> Light
              </CDropdownItem>
              <CDropdownItem
                active={colorMode === 'dark'}
                className="d-flex align-items-center"
                as="button"
                type="button"
                onClick={() => setColorMode('dark')}
              >
                <CIcon className="me-2" icon={cilMoon} size="lg" /> Dark
              </CDropdownItem>
              <CDropdownItem
                active={colorMode === 'auto'}
                className="d-flex align-items-center"
                as="button"
                type="button"
                onClick={() => setColorMode('auto')}
              >
                <CIcon className="me-2" icon={cilContrast} size="lg" /> Auto
              </CDropdownItem>
            </CDropdownMenu>
          </CDropdown>
          <li className="nav-item py-1">
            <div className="vr h-100 mx-2 text-body text-opacity-75"></div>
          </li>
          <AppHeaderDropdown />
        </CHeaderNav>
      </CContainer>
    </CHeader>
  )
}

export default AppHeader
